(ns app.reels.page
  "Replicant page for publishing existing gallery videos as Instagram Reels."
  (:require [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.date.datetime-local :as datetime-local]
            [storrito.state.local-storage :as local-storage]
            [ui.app-frame :as app-frame]
            [ui.gallery.link :as gallery-link]
            [ui.story-publish.components :as story-publish]))

(defn- reels-publish-query
  [state]
  (when-let [eid (::story-media-eid state)]
    {:query/kind :query/reels-publish
     :query/refresh? true
     :query/data (cond-> {:story-media/eid eid}
                   (::reel-eid state)
                   (assoc :reel/eid (::reel-eid state)))}))

(defn- date-ms
  [date-str]
  (when (seq date-str)
    (let [date-ms (.getTime (js/Date. date-str))]
      (when-not (js/isNaN date-ms)
        date-ms))))

(defn- selected-accounts
  [state]
  (vec (or (::selected-accounts state) [])))

(defn- edit-mode?
  "The page was opened for a scheduled Reel that can still be changed."
  [result]
  (boolean (get-in result [:reel/scheduled :reel/editable?])))

(defn- caption
  "The store holds nil until the user types, so that the caption of the
   scheduled Reel shows up once the query result arrives."
  [state result]
  (or (::caption state)
      (get-in result [:reel/scheduled :reel/caption])
      ""))

(defn- option-checked?
  "Checkbox states are stored as `:on` / `:off`, since the Replicant action
   enricher turns a `false` in the action data into nil."
  [state result store-key reel-key default]
  (let [reel (:reel/scheduled result)]
    (case (get state store-key)
      :on true
      :off false
      (if (contains? reel reel-key)
        (boolean (get reel reel-key))
        default))))

(defn- recommend-facebook?
  [state result]
  (option-checked? state result ::recommend-facebook :reel/recommend-facebook true))

(defn- recommend-feed?
  [state result]
  (option-checked? state result ::recommend-feed :reel/recommend-feed false))

(defn- schedule-date-str
  [state result]
  (or (::schedule-date-str state)
      (when (edit-mode? result)
        (datetime-local/format-date
          (js/Date. (get-in result [:reel/scheduled :reel/post-date]))))
      (story-publish/current-datetime-local)))

(defn- post-date-ms
  [state result]
  (or (date-ms (schedule-date-str state result))
      (.getTime (js/Date.))))

(defn- schedule-command
  [state result account-id]
  {:command/kind :command/reel-schedule
   :command/data {:instagram.user/id account-id
                  :story-media/eid (:story-media/eid result)
                  :blob/uuid (:reels-publish/video-blob-id result)
                  :blob/name (:reels-publish/video-blob-name result)
                  :blob/preview (:reels-publish/preview-blob-id result)
                  :instagram/caption (caption state result)
                  :instagram/recommend-facebook (recommend-facebook? state result)
                  :instagram/recommend-feed (recommend-feed? state result)
                  :reel/post-date (post-date-ms state result)}})

(defn- update-command
  [state result]
  {:command/kind :command/reel-update
   :command/data {:db/id (get-in result [:reel/scheduled :db/id])
                  :instagram/caption (caption state result)
                  :instagram/recommend-facebook (recommend-facebook? state result)
                  :instagram/recommend-feed (recommend-feed? state result)
                  :reel/post-date (post-date-ms state result)}})

(defn- commands
  [state result]
  (if (edit-mode? result)
    [(update-command state result)]
    (mapv #(schedule-command state result (:instagram.user/id %))
          (selected-accounts state))))

(defn- command-key
  [cmd]
  (str (:command/kind cmd)
       "-"
       (or (get-in cmd [:command/data :instagram.user/id])
           (get-in cmd [:command/data :db/id]))))

(defn- scheduled-account
  "A scheduled Reel stays on its Instagram account, so the edit mode shows the
   account instead of the account selector."
  [result]
  (let [user-id (get-in result [:reel/scheduled :reel/user-id])]
    [:div {:class ["space-y-1"]}
     [:h3 {:class ["text-sm" "font-semibold" "text-gray-700"]} "Instagram Account"]
     [:p {:class ["text-sm" "text-gray-800"]}
      (str "@" (story-publish/user-id->name (:instagram/users result) user-id))]
     [:p {:class ["text-xs" "text-gray-500"]}
      "You are editing a scheduled Reel. Changes apply to this Reel, no second Reel is created."]]))

(defn- checkbox
  [{:keys [label description store-key checked]}]
  [:label
   {:class ["flex" "items-start" "gap-3" "py-1.5" "text-sm" "cursor-pointer"]}
   [:input {:type "checkbox"
            :checked (boolean checked)
            :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300" "text-gray-900"]
            :on {:click [[:store/assoc store-key (if checked :off :on)]
                         [:store/assoc ::scheduled nil]]}}]
   [:span
    [:span {:class ["block" "font-medium" "text-gray-800"]} label]
    (when description
      [:span {:class ["block" "text-xs" "text-gray-500"]} description])]])

(defn- publish-form
  [state result]
  (let [q (reels-publish-query state)
        edit? (edit-mode? result)
        date-str (schedule-date-str state result)
        is-future (story-publish/date-in-future? date-str)
        cmds (commands state result)
        issued? (some #(command/issued? state %) cmds)
        has-credits? (or (:organization/positive-credit-balance? result)
                         (:organization/legacy-customer? result))
        scheduled? (::scheduled state)
        can-publish? (and (seq cmds)
                          (or edit?
                              (and (:story-media/reel-ready? result)
                                   has-credits?))
                          (not scheduled?))]
    [:div {:class ["flex-1" "space-y-6"]}
     (if edit?
       (scheduled-account result)
       (story-publish/account-selector {:state state
                                        :users (:instagram/users result)
                                        :selected-key ::selected-accounts
                                        :dropdown-open-key ::account-dropdown-open
                                        :title "Instagram Accounts"
                                        :save-selection? true
                                        :on-change-actions [[:store/assoc ::scheduled nil]]}))

     [:div {:class ["space-y-2"]}
      [:label {:class ["text-sm" "font-semibold" "text-gray-700"]} "Caption"]
      [:textarea {:value (caption state result)
                  :rows 4
                  :placeholder "Add a caption..."
                  :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                          "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                          "focus:border-indigo-500" "resize-none"]
                  :on {:input [[:store/assoc ::caption :event/target.value]
                               [:store/assoc ::scheduled nil]]}}]]

     [:div {:class ["space-y-3"]}
      [:h3 {:class ["text-sm" "font-semibold" "text-gray-700"]} "Reel Options"]
      [:div {:class ["grid" "gap-2" "sm:grid-cols-2"]}
       (checkbox {:label "Recommend on Facebook"
                  :description "Let Instagram recommend the Reel on Facebook"
                  :checked (recommend-facebook? state result)
                  :store-key ::recommend-facebook})
       (checkbox {:label "Share to Instagram main Feed"
                  :description "Also show the Reel in the profile feed"
                  :checked (recommend-feed? state result)
                  :store-key ::recommend-feed})]]

     [:div {:class ["space-y-4"]}
      [:h3 {:class ["text-sm" "font-semibold" "text-gray-700"]} "Date & Time"]
      [:input {:type "datetime-local"
               :value date-str
               :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                       "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                       "focus:border-indigo-500"]
               :on {:input [[:store/assoc ::schedule-date-str :event/target.value]
                            [:store/assoc ::scheduled nil]]}}]

      (when (and is-future
                 (not scheduled?)
                 (:organization/positive-credit-balance? result)
                 (not (:organization/legacy-customer? result))
                 (not (:organization/free-account? result)))
        (story-publish/future-credit-warning result "Reels"))

      (when (and (:story-media/reel-ready? result)
                 (not (:story-media/video-url result)))
        [:div {:class ["p-3" "rounded-lg" "bg-blue-50" "border" "border-blue-200"]}
         [:p {:class ["text-sm" "text-blue-800"]}
          "The video will be rendered automatically before posting."]])

      (when-not (:story-media/reel-ready? result)
        [:div {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
         [:p {:class ["text-sm" "text-amber-800"]}
          "This gallery item cannot be posted as a Reel."]])

      (when-not has-credits?
        [:div {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
         [:p {:class ["text-sm" "text-amber-800"]}
          "Your credit balance is empty. "
          [:button
           {:class ["font-medium" "text-amber-900" "underline" "cursor-pointer"]
            :on {:click [[:data/query {:query/kind :query/user-session}]
                         [:ui/modal-open :modals/credits-exhausted {}]]}}
           "Top up credits"]
          " to publish Reels."]])

      (when scheduled?
        [:div {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
         [:p {:class ["text-sm" "text-green-800" "font-medium"]}
          (cond
            (not is-future) "Reel is being posted!"
            edit? "Scheduled Reel updated!"
            :else "Reel scheduled successfully!")]])

      (when (seq cmds)
        [:div {:class ["space-y-2"]}
         (map
           (fn [cmd]
             (when (command/error? state cmd)
               [:div {:class ["p-3" "rounded-lg" "bg-red-50" "border" "border-red-200"]
                      :replicant/key (command-key cmd)}
                [:p {:class ["text-sm" "text-red-800"]}
                 (or (command/get-latest-error state cmd)
                     (if edit?
                       "Failed to update the Reel. Please try again."
                       "Failed to schedule Reel. Please try again."))]]))
           cmds)])

      [:button {:class (into ["w-full" "py-2.5" "px-4" "text-sm" "font-semibold" "rounded-lg"
                              "transition-colors" "cursor-pointer"]
                             (if can-publish?
                               ["bg-indigo-600" "text-white" "hover:bg-indigo-700"]
                               ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
                :disabled (or (not can-publish?)
                              issued?)
                :on {:click (when can-publish?
                              (vec
                                (map (fn [cmd]
                                       [:data/command cmd
                                        {:on-success [[:store/assoc ::scheduled true]
                                                      [:data/query q]]
                                         :on-error [[:ui/show-error]]}])
                                     cmds)))}}
       (cond
         issued? (if edit? "Updating..." "Scheduling...")
         scheduled? (cond
                      (not is-future) "Posted"
                      edit? "Updated"
                      :else "Scheduled")
         is-future (if edit? "Update scheduled Reel" "Schedule Reel")
         :else "Post Reel now")]]]))

(defn- no-media-selected
  "The page opened without a gallery video (a bookmark or a typed
   `/ui/reels-publish`): says so instead of waiting for a query that
   never starts."
  [state]
  (app-frame/app-frame
    state
    [:div {:class ["max-w-4xl" "mx-auto" "py-12" "text-center"]}
     [:p {:class ["text-gray-500"]}
      "No video selected. Open a video from the gallery, or use the Edit button of a scheduled Reel on the schedule, to publish it as a Reel."]
     [:p {:class ["mt-4"]}
      [:a {:href (gallery-link/gallery-href)
           :class ["text-indigo-600" "hover:text-indigo-700"]}
       "Go to the gallery"]]]))

(defn render
  [state]
  (let [q (reels-publish-query state)
        result (when q (query/get-result state q))
        loading? (and q
                      (query/loading? state q)
                      (not (query/available? state q)))
        derived-state (if (and (nil? (::selected-accounts state))
                               (seq (:instagram/users result)))
                        (assoc state ::selected-accounts
                               (story-publish/auto-select-first-account
                                 (:instagram/users result)
                                 (::saved-ig-user-id state)))
                        state)]
    (cond
      (not q)
      (no-media-selected state)

      loading?
      [:div {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]

      :else
      (app-frame/app-frame
        derived-state
        [:div {:class ["max-w-4xl" "mx-auto" "overflow-x-hidden"]}
         [:div {:class ["mb-4"]}
          [:h1 {:class ["text-xl" "font-bold" "text-gray-900"]}
           (or (:story-media/name result) "Reel")]
          [:p {:class ["text-sm" "text-gray-500" "mt-1"]}
           [:a {:href "/ui/agenda"
                :class ["text-indigo-600" "hover:text-indigo-700"]}
            "Back to schedule"]]]
         [:div {:class ["flex" "flex-col" "sm:flex-row" "gap-6"]}
          (story-publish/preview-section result)
          (publish-form derived-state result)]]))))

(defn- parse-eid
  [eid-str]
  (when eid-str
    (let [n (js/parseInt eid-str 10)]
      (when-not (js/isNaN n)
        n))))

(defn on-load
  [location]
  (let [eid (parse-eid (get-in location [:location/query-params :story-eid]))
        reel-eid (parse-eid (get-in location [:location/query-params :reel-eid]))
        saved-ig-user-id (:instagram.user/id @local-storage/state)]
    (if-not eid
      ;; Forget the video of an earlier visit, so that an in-app navigation
      ;; to the bare URL shows the "no video" message, not the old form:
      [[:store/assoc ::story-media-eid nil]
       [:store/assoc ::reel-eid nil]]
      (cond-> [[:ui/hide-sidebar-on-mobile]
               [:store/assoc ::story-media-eid eid]
               [:store/assoc ::reel-eid reel-eid]
               [:store/assoc ::selected-accounts nil]
               [:store/assoc ::caption nil]
               [:store/assoc ::recommend-facebook nil]
               [:store/assoc ::recommend-feed nil]
               [:store/assoc ::scheduled nil]
               [:store/assoc ::schedule-date-str nil]
               [:data/query {:query/kind :query/reels-publish
                             :query/refresh? true
                             :query/data (cond-> {:story-media/eid eid}
                                           reel-eid
                                           (assoc :reel/eid reel-eid))}]]
        saved-ig-user-id
        (conj [:store/assoc ::saved-ig-user-id saved-ig-user-id])))))

(def page
  {:page-id :pages/reels-publish
   :route [["ui" "reels-publish"]]
   :on-load on-load
   :render #'render})
